import React, { useEffect } from 'react'
import { Button, Col, Image, Modal, Row, Card, Badge, ProgressBar, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, 
    faTabletAlt, 
    faBatteryFull, 
    faDownload, 
    faClock, 
    faCheckCircle, 
    faTimesCircle,
    faPhone,
    faIdCard,
    faEnvelope,
    faBuilding,
    faMemory,
    faMobile,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { ClientService } from '../services/client.service';

interface IPreviewModal {
    show: any,
    handleClose: any
}

export default function InvigilatorModal(props: IPreviewModal) {

    const [data, setData] = React.useState<any>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    async function getInvigilatorData(params: any) {
        if (!props.show || props.show === '') return;
        setLoading(true);
        await ClientService.getInvigilatorDetails(params)
            .then((res: any) => {
                setData(res.data);
                setLoading(false);
            })
            .catch((err: any) => {
                setLoading(false);
                console.error("Error fetching invigilator data:", err);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const getBatteryColor = (percentage: number) => {
        if (percentage > 50) return 'success';
        if (percentage > 20) return 'warning';
        return 'danger';
    };

    const getDeviceInfo = (deviceInfoString: string) => {
        try {
            return JSON.parse(deviceInfoString);
        } catch {
            return {};
        }
    };

    const deviceInfo = data?.invigilator?.meta?.devices?.length > 0
        ? getDeviceInfo(data?.invigilator?.meta?.devices[0]?.dev_info)
        : {};

    useEffect(() => {
        if (props.show) {
            getInvigilatorData(props.show);
        }
    }, [props.show]);
    return (
        <Modal
            show={props.show ? true : false}
            onHide={props.handleClose}
            size="xl"
            animation={true}
            backdrop="static"
            centered
            className="invigilator-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="text-dark">
                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                    Invigilator Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" className="pulse-animation" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted fs-5">Loading invigilator data...</p>
                    </div>
                )}
                
                {!loading && !data && (
                    <Alert variant="warning" className="text-center py-4">
                        <FontAwesomeIcon icon={faTimesCircle} size="3x" className="mb-3 text-warning" />
                        <h5 className="mb-0">No invigilator data available</h5>
                        <p className="text-muted mb-0">Please try again or contact support.</p>
                    </Alert>
                )}
                
                {!loading && data && data?.invigilator && (
                    <div className="p-2">
                        <Row className="mb-4 g-3">
                            <Col lg={3} md={6}>
                                <Card className="h-100 border-0 shadow-sm stats-card">
                                    <Card.Body className="text-center p-4">
                                        <div className="d-flex flex-column align-items-center">

                                        <FontAwesomeIcon 
                                            icon={data?.invigilator.isAllowed ? faCheckCircle : faTimesCircle} 
                                            className={`mb-3 ${data?.invigilator.isAllowed ? 'text-success' : 'text-danger'}`}
                                            size="3x"
                                            />
                                        <Badge 
                                            bg={data?.invigilator.isAllowed ? 'success' : 'danger'} 
                                            className="mb-3 px-3 py-2 fs-6"
                                            >
                                            {data?.invigilator.isAllowed ? 'ALLOWED' : 'BLOCKED'}
                                        </Badge>
                                        <Card.Title className="h6 mb-0 text-dark">Access Status</Card.Title>
                                            </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={3} md={6}>
                                <Card className="h-100 border-0 shadow-sm stats-card">
                                    <Card.Body className="text-center p-4">
                                        <FontAwesomeIcon icon={faBatteryFull} className="text-primary mb-3" size="3x" />
                                        <div className="mb-3">
                                            <ProgressBar 
                                                now={Math.max(0, data?.invigilator.meta.battery.percentage)} 
                                                variant={getBatteryColor(data?.invigilator.meta.battery.percentage)}
                                                style={{ height: '10px' }}
                                                className="mb-2"
                                            />
                                            <div className="h4 text-dark mb-0">{Math.round(data?.invigilator.meta.battery.percentage)}%</div>
                                        </div>
                                        <Card.Title className="h6 mb-0 text-dark">Battery Level</Card.Title>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={3} md={6}>
                                <Card className="h-100 border-0 shadow-sm stats-card">
                                    <Card.Body className="text-center p-4">
                                        <FontAwesomeIcon icon={faDownload} className="text-success mb-3" size="3x" />
                                        <div className="mb-3">
                                            <ProgressBar 
                                                now={data?.invigilator.meta.downloadStats.percentage} 
                                                variant="success"
                                                style={{ height: '10px' }}
                                                className="mb-2"
                                            />
                                            <div className="h4 text-dark mb-0">{data?.invigilator.meta.downloadStats.percentage}%</div>
                                        </div>
                                        <Card.Title className="h6 mb-0 text-dark">Download Progress</Card.Title>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={3} md={6}>
                                <Card className="h-100 border-0 shadow-sm stats-card">
                                    <Card.Body className="text-center p-4">
                                        <FontAwesomeIcon icon={faClock} className="text-info mb-3" size="3x" />
                                        <div className="h2 text-info mb-2">{data?.invigilator.meta.loginTimes.length}</div>
                                        <Card.Title className="h6 mb-0 text-dark">Login Sessions</Card.Title>
                                        <small className="text-muted">Total Count</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            {/* Invigilator Details */}
                            <Col lg={6} className="mb-4">
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Header className="bg-primary text-white">
                                        <Card.Title className="mb-0 h5">
                                            <FontAwesomeIcon icon={faUser} className="me-2" />
                                            Invigilator Details
                                        </Card.Title>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col sm={6}>
                                                <div className="border-bottom pb-2 mb-2">
                                                    <small className="text-muted d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faUser} className="me-2" />
                                                        Username
                                                    </small>
                                                    <div className="fw-bold">{data?.invigilator.userName}</div>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="border-bottom pb-2 mb-2">
                                                    <small className="text-muted d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faIdCard} className="me-2" />
                                                        Operator Name
                                                    </small>
                                                    <div className="fw-bold">{data?.invigilator.operatorName}</div>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="border-bottom pb-2 mb-2">
                                                    <small className="text-muted d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                                        Email
                                                    </small>
                                                    <div className="fw-bold text-break">{data?.invigilator.emailId}</div>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="border-bottom pb-2 mb-2">
                                                    <small className="text-muted d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                                                        Operator ID
                                                    </small>
                                                    <div className="fw-bold">{data?.invigilator.operatorId}</div>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="border-bottom pb-2 mb-2">
                                                    <small className="text-muted d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                                        Center ID
                                                    </small>
                                                    <Badge bg="secondary" className="fs-6">{data?.invigilator.centerId[0]}</Badge>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="border-bottom pb-2 mb-2">
                                                    <small className="text-muted">Exam ID</small>
                                                    <div className="small text-muted text-break">{data?.invigilator.examId}</div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Device Information */}
                            <Col lg={6} className="mb-4">
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Header className="bg-info text-white">
                                        <Card.Title className="mb-0 h5">
                                            <FontAwesomeIcon icon={faTabletAlt} className="me-2" />
                                            Device Information
                                        </Card.Title>
                                    </Card.Header>
                                    <Card.Body>
                                        {deviceInfo.deviceName ? (
                                            <Row className="g-3">
                                                <Col sm={6}>
                                                    <div className="border-bottom pb-2 mb-2">
                                                        <small className="text-muted d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faMobile} className="me-2" />
                                                            Device
                                                        </small>
                                                        <div className="fw-bold">{deviceInfo.deviceName}</div>
                                                    </div>
                                                </Col>
                                                <Col sm={6}>
                                                    <div className="border-bottom pb-2 mb-2">
                                                        <small className="text-muted">Brand</small>
                                                        <div className="fw-bold">{deviceInfo.brand}</div>
                                                    </div>
                                                </Col>
                                                <Col sm={6}>
                                                    <div className="border-bottom pb-2 mb-2">
                                                        <small className="text-muted">Operating System</small>
                                                        <div className="fw-bold">{deviceInfo.osName} {deviceInfo.osVersion}</div>
                                                    </div>
                                                </Col>
                                                <Col sm={6}>
                                                    <div className="border-bottom pb-2 mb-2">
                                                        <small className="text-muted">Device Type</small>
                                                        <Badge bg="primary">
                                                            {deviceInfo.deviceType === 2 ? 'Tablet' : 'Unknown'}
                                                        </Badge>
                                                    </div>
                                                </Col>
                                                <Col xs={12}>
                                                    <div className="border-bottom pb-2 mb-2">
                                                        <small className="text-muted d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faMemory} className="me-2" />
                                                            Total Memory
                                                        </small>
                                                        <div className="fw-bold">
                                                            {Math.round(deviceInfo.totalMemory / (1024 * 1024 * 1024))} GB
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        ) : (
                                            <Alert variant="light" className="text-center mb-0">
                                                <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                                No device information available
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Activity Timeline */}
                        <Row className="mb-4">
                            <Col xs={12}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-success text-white">
                                        <Card.Title className="mb-0 h5">
                                            <FontAwesomeIcon icon={faClock} className="me-2" />
                                            Activity Timeline
                                        </Card.Title>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Card className="border-0 bg-light h-100">
                                                    <Card.Body>
                                                        <Card.Subtitle className="text-muted mb-3">Last Login Time</Card.Subtitle>
                                                        <div className="d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                                                            <small>{formatDate(data?.invigilator.meta.loginTimes.slice(-1)[0])}</small>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6}>
                                                <Card className="border-0 bg-light h-100">
                                                    <Card.Body>
                                                        <Card.Subtitle className="text-muted mb-3">System Information</Card.Subtitle>
                                                        <Row className="g-2">
                                                            <Col sm={6}>
                                                                <small className="text-muted">Last Synced</small>
                                                                <div className="small fw-bold">{formatDate(data?.invigilator.lastSyncedOn)}</div>
                                                            </Col>
                                                            <Col sm={6}>
                                                                <small className="text-muted">Last Download</small>
                                                                <div className="small fw-bold">{formatDate(data?.invigilator.lastDownloadAt)}</div>
                                                            </Col>
                                                            <Col sm={6}>
                                                                <small className="text-muted">Battery Updated</small>
                                                                <div className="small fw-bold">{formatDate(data?.invigilator.meta.battery.updatedAt)}</div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Additional Operator Details */}
                        {data?.operatorDetails && data?.operatorDetails.length > 0 && (
                            <Row>
                                <Col xs={12}>
                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-warning text-dark">
                                            <Card.Title className="mb-0 h5">
                                                <FontAwesomeIcon icon={faIdCard} className="me-2" />
                                                Additional Operator Details
                                            </Card.Title>
                                        </Card.Header>
                                        <Card.Body>
                                            {data?.operatorDetails?.map((detail: any, index: number) => (
                                                <Card key={index} className="mb-3 border">
                                                    <Card.Body>
                                                        <Row className="align-items-center">
                                                            <Col md={8}>
                                                                <Row className="g-3">
                                                                    <Col sm={4}>
                                                                        <small className="text-muted d-flex align-items-center">
                                                                            <FontAwesomeIcon icon={faUser} className="me-2" />
                                                                            Name
                                                                        </small>
                                                                        <div className="fw-bold">{detail?.name}</div>
                                                                    </Col>
                                                                    <Col sm={4}>
                                                                        <small className="text-muted d-flex align-items-center">
                                                                            <FontAwesomeIcon icon={faPhone} className="me-2" />
                                                                            Phone
                                                                        </small>
                                                                        <div className="fw-bold">{detail?.mobile}</div>
                                                                    </Col>
                                                                    <Col sm={4}>
                                                                        <small className="text-muted d-flex align-items-center">
                                                                            <FontAwesomeIcon icon={faIdCard} className="me-2" />
                                                                            Aadhaar Number
                                                                        </small>
                                                                        <div className="fw-bold">{detail?.aadhaarNumber}</div>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                            <Col md={4}>
                                                                <Row>
                                                                    <Col xs={6}>
                                                                        <div className="text-center">
                                                                            <small className="text-muted d-block mb-2">Photo</small>
                                                                            <Image 
                                                                                src={detail?.photo} 
                                                                                alt={detail?.name} 
                                                                                fluid 
                                                                                rounded
                                                                                style={{ maxHeight: '80px', objectFit: 'cover' }}
                                                                            />
                                                                        </div>
                                                                    </Col>
                                                                    <Col xs={6}>
                                                                        <div className="text-center">
                                                                            <small className="text-muted d-block mb-2">Aadhaar</small>
                                                                            <Image 
                                                                                src={detail?.aadhaarImage} 
                                                                                alt="Aadhaar" 
                                                                                fluid 
                                                                                rounded
                                                                                style={{ maxHeight: '80px', objectFit: 'cover' }}
                                                                            />
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="outline-secondary" onClick={props.handleClose} className="px-4 py-2">
                    <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                    Close
                </Button>
            </Modal.Footer>
        </Modal >
    )
}
